// Native
import { useEffect, useState } from "react"

// Components
import { AlertDialogBody, AlertDialogFooter, Badge, Button, ButtonGroup, Center, Divider, Flex, HStack, Td, Text, Tr, useDisclosure, useToast } from "@chakra-ui/react"
import { Dialog, Icon, IconButton, Lottie, Section, Shipment, Table, Tag } from "components"

// Config
import { API } from "config"

// Hooks
import { useOrder } from "hooks"

// Screens
import Volume from "./volume"
import Select from "./select"
import Manual from "./manual"

export default function Quotation({ data }){

    const toast = useToast()
    const order = useOrder(data?.id)
    const volume = useDisclosure()
    const confirm = useDisclosure()
    const manual = useDisclosure()
    const [ loading, setLoading ] = useState(false)
    const [ quotationList, setQuotationList ] = useState()
    const [ quotation, setQuotation ] = useState()

    const columns = [{ children: 'Rótulo' }, { children: 'Altura', isNumeric: true }, { children: 'Largura', isNumeric: true }, { children: 'Comprimento', isNumeric: true }, { children: 'Peso', isNumeric: true }, { isNumeric: true }]

    const handleRemove = async (volume) => {
        if(!window.confirm('Tem certeza que deseja remover este volume da cotação?')) return
        setLoading(true)

        try{
            await API.delete(`/orders/${order.data.id}/volumes/${volume.id}`)
            toast({ title: 'O volume foi removido.', status: 'info' })
            order.refetch()
        }catch(err){
            toast({ title: 'Não foi possível remover o volume.', description: err?.response?.data?.message, status: 'warning' })
        }

        setLoading(false)
    }

    const sync = async () => {
        if(!order.data?.volumes?.length) return
        setLoading(true)

        try{
            const callback = await API.get(`/orders/${order.data.id}/quotation`)
            setQuotationList(callback.data)
        }catch(err){
            setQuotationList(false)
            toast({ title: 'Não foi possível realizar a cotação agora. Tente novamente', description: err?.response?.data?.message, status: 'warning' })
        }

        setLoading(false)
    }

    useEffect(() => {
        sync()
    }, [order.data?.volumes])

    return(
    <>
    <Section title="Volumes para cotação">
        <ButtonGroup size="xs">
            <Button children="Cotação manual" onClick={manual.onOpen} />
            <Button leftIcon={<Icon name="add" />} colorScheme="blue" children="Adicionar volume" onClick={volume.onOpen} />
        </ButtonGroup>
    </Section>

    <Table variant="striped" columns={columns}>
        { order.data?.volumes?.map((item, index) => (
            <Tr key={item.id}>
                <Td><Badge>Volume 0{index+1}</Badge></Td>
                <Td isNumeric><Tag children={`${item.width} cm`} /></Td>
                <Td isNumeric><Tag children={`${item.height} cm`} /></Td>
                <Td isNumeric><Tag children={`${item.length} cm`} /></Td>
                <Td isNumeric><Tag children={`${item.weight} kg`} /></Td>
                <Td isNumeric><IconButton label="Remover" isDisabled={loading} icon="trash" colorScheme="red" variant="outline" size="xs" onClick={() => handleRemove(item)} /></Td>
            </Tr>
        )) }
    </Table>

    <Divider my={6} />

    <Section title="Resultado da cotação:" />
    <Text children="Melhor Envio" mb={4} />

    <HStack>
        { quotationList?.melhor_envio?.map(item => <Shipment key={item.id} data={item} onClick={() => setQuotation(item)} />) }
    </HStack>

    <Divider my={6} />

    <Flex maxW="40%" justifyContent="center" alignItems="center" direction="column" margin="30px auto">
        <Lottie animation="quotation" options={{ loop: true }} style={{ width: 200 }} />
        <Text fontSize="lg" fontWeight="bold" children="Em cotação" />
        <Text textAlign="center" color="GrayText">O pedido está sendo cotado no momento. Ao finalizar, prossiga para a solicitação de nota fiscal.</Text>
        <Button isDisabled={!order.data?.shipment} children="Fechar cotação e solicitar nota" colorScheme="orange" leftIcon={<Icon name="fiscal" />} rightIcon={<Icon name="right" />} size="lg" mt={4} onClick={confirm.onOpen} />
    </Flex>

    {/* Modals */}
    <Volume data={order.data} {...volume} onSuccess={order.refetch} />
    <Manual data={order.data} {...manual} onSuccess={order.refetch} />
    <Select isOpen={!!quotation} data={quotation} order={order} onClose={() => setQuotation(false)} onSuccess={order.refetch} />
    <ConfirmAlert data={order} onSuccess={order.refetch} {...confirm} />
    </>
    )
}

const ConfirmAlert = ({ data, onSuccess = () => {}, ...props }) => {

    const toast = useToast()
    const [ loading, setLoading ] = useState(false)

    const handleSubmit = async () => {
        setLoading(true)

        try{
            await API.put(`/orders/${data.data?.id}/fiscal`)
            toast({ title: 'A nota fiscal foi solicitada com sucesso!', status: 'success' })
            props.onClose()
            onSuccess()
        }catch(err){
            toast({ title: 'Não foi possível continuar', description: err?.response?.data?.message, status: 'warning' })
        }

        setLoading(false)
    }

    return(
    <Dialog title="Fechar cotação" loading={loading} {...props}>
        <AlertDialogBody fontSize="sm">
            <Text>Tem certeza que deseja fechar a cotação deste pedido e prosseguir para a nota fiscal?</Text>
        </AlertDialogBody>
        <AlertDialogFooter>
            <ButtonGroup size="xs" isDisabled={loading}>
                <Button children="Fechar cotação" isLoading={loading} onClick={handleSubmit} />
                <Button children="Cancelar" colorScheme="red" onClick={props.onClose} />
            </ButtonGroup>
        </AlertDialogFooter>
    </Dialog>
    )
}